import HttpClient from '../../../core/HttpClient';
import Failure from '../../../core/Failure';
import { Authentication } from '@leedpulse-monorepo/classes';

export interface ConsumptionMovement {
  id: string;
  type_ref: string | null;
  created: number | null;
  units: number | null;
  units_type: string | null;
  credit_consumed: number;
  id_contact: string | null;
  contact_firstname: string | null;
  contact_lastname: string | null;
  over_plan: boolean;
  overage_amount: number;
  billing?: MovementBilling | null; // destination-priced call: money, no credits
  reason_label?: string; // manual adjustment: the reason shown to the client
}

// Money billing of an outbound call priced by destination
export interface MovementBilling {
  mode: 'destination';
  status: string;
  country: string; // ISO2
  country_label: string;
  line_type: 'landline' | 'cellular' | 'unknown';
  units: number; // billable minutes
  unit_price: number; // EUR per minute
  amount: number; // EUR
  currency: string;
}

export interface ConsumptionCycle {
  start: number;
  end: number;
  is_current: boolean;
}

export interface CycleSummary {
  included?: number; // what the cycle was granted, promotional or not
  consumed: number;
  granted?: number; // credits added by hand during the cycle, negative when lowered
  overage_credits: number;
  overage_amount: number;
  destination?: { calls: number; billable_minutes: number; amount: number } | null; // null until the index exists
  aggregates: {
    call_minutes: number;
    sms_sent: number;
    voicemails: number;
  };
}

export interface ConsumptionMovementsPage {
  movements: ConsumptionMovement[];
  next_cursor: number | null;
  overage_remaining: number;
  summary?: CycleSummary | null;
}

export interface ConsumptionHistory {
  included: number;
  remaining: number;
  payg_rate: number;
  promo_until?: number | null; // end (excluded) of the promo that granted the current cycle, while it runs
  cycles: ConsumptionCycle[];
}

export async function getSubscriptionConsumptionHistory(
  subscriptionId: string,
  companyApiKey: string,
  auth: Authentication,
): Promise<ConsumptionHistory> {
  try {
    const response = await HttpClient.get(
      `/leedflow/subscriptions/${encodeURIComponent(subscriptionId)}/consumption-history`,
      auth.token,
      {
        'x-api-key': companyApiKey,
      },
    );

    return (response?.data ?? { included: 0, remaining: 0, payg_rate: 0, cycles: [] }) as ConsumptionHistory;
  } catch (e) {
    console.error('getSubscriptionConsumptionHistory error:', e);
    throw new GetSubscriptionConsumptionHistoryFailure((e as Error)?.message || 'Unknown error');
  }
}

export async function getCycleMovements(
  subscriptionId: string,
  companyApiKey: string,
  auth: Authentication,
  params: { cycleStart: number; cycleEnd: number; cursor?: number | null; limit?: number; overageRemaining?: number },
): Promise<ConsumptionMovementsPage> {
  try {
    const query = [
      `cycleStart=${params.cycleStart}`,
      `cycleEnd=${params.cycleEnd}`,
      `limit=${params.limit ?? 100}`,
      `overageRemaining=${params.overageRemaining ?? 0}`,
    ];
    if (params.cursor != null) query.push(`cursor=${params.cursor}`);

    const response = await HttpClient.get(
      `/leedflow/subscriptions/${encodeURIComponent(subscriptionId)}/consumption-history/movements?${query.join('&')}`,
      auth.token,
      {
        'x-api-key': companyApiKey,
      },
    );

    return (response?.data ?? { movements: [], next_cursor: null, overage_remaining: 0, summary: null }) as ConsumptionMovementsPage;
  } catch (e) {
    console.error('getCycleMovements error:', e);
    throw new GetSubscriptionConsumptionHistoryFailure((e as Error)?.message || 'Unknown error');
  }
}

export class GetSubscriptionConsumptionHistoryFailure extends Failure {
  constructor(message = 'Unknown error', code = 'get-subscription-consumption-history-failure') {
    super(message, code);
  }
}
